import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import '../styles/MedicalArticles.css';

const MedicalArticles = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('الكل');

  const categories = ['الكل', 'أمراض القلب', 'التغذية', 'الصحة النفسية', 'الأطفال', 'الجراحة'];

  // دالة لتصحيح مسارات الصور المكررة
  const getCorrectImagePath = (imagePath) => {
    if (!imagePath) return null;
    
    const baseUrl = 'https://darkslateblue-eel-151368.hostingersite.com';
    
    if (imagePath.startsWith('http')) {
      // إصلاح المسارات المكررة
      const corrected = imagePath.replace(
        /uploads\/articles\/uploads\/articles\//g,
        'uploads/articles/'
      );
      return corrected;
    }
    
    if (imagePath.startsWith('uploads/')) {
      return `${baseUrl}/storage/${imagePath}`;
    }
    
    if (imagePath.startsWith('storage/')) {
      return `${baseUrl}/${imagePath}`;
    }
    
    return `${baseUrl}/storage/${imagePath}`;
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('https://darkslateblue-eel-151368.hostingersite.com/api/articles', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('فشل في تحميل المقالات');
      }
      
      const result = await response.json();
      
      // التعامل مع أنواع البيانات المختلفة
      let data;
      if (result.success && result.data) {
        data = result.data;
      } else if (result.data) {
        data = result.data;
      } else if (Array.isArray(result)) {
        data = result;
      } else {
        data = [];
      }
      
      // تصحيح مسارات الصور
      if (Array.isArray(data)) {
        data = data.map(article => ({
          ...article,
          image: getCorrectImagePath(article.image)
        }));
      }
      
      setArticles(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('فشل في تحميل المقالات. يرجى المحاولة مرة أخرى.');
      console.error('Error fetching articles:', err);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchTerm(query);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleViewDetails = (articleId) => {
    navigate(`/articles/${articleId}`);
  };

  // تصفية المقالات محلياً للبحث والتصنيف
  const filteredArticles = Array.isArray(articles) 
    ? articles.filter(article => {
        if (!article) return false;
        
        const title = (article.title || '').toLowerCase();
        const description = (article.description || '').toLowerCase();
        const articleCategory = article.category || '';
        const searchLower = searchTerm.toLowerCase();
        
        const matchesSearch = searchTerm === '' || 
          title.includes(searchLower) || 
          description.includes(searchLower);
        
        const matchesCategory = selectedCategory === 'الكل' || 
          articleCategory === selectedCategory;
        
        return matchesSearch && matchesCategory;
      })
    : [];

  if (error) {
    return (
      <div className="medical-articles-page" dir="rtl">
        <header className="articles-header">
          <div className="articles-header-container">
            <h1 className="articles-header-title">المقالات الطبية</h1>
            <p className="articles-header-subtitle">مقالات طبية متخصصة ومعلومات صحية موثوقة</p>
          </div>
        </header>
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchArticles} className="retry-button">
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="medical-articles-page" dir="rtl">
      <header className="articles-header">
        <div className="articles-header-container">
          <h1 className="articles-header-title">المقالات الطبية</h1>
          <p className="articles-header-subtitle">مقالات طبية متخصصة ومعلومات صحية موثوقة</p>
        </div>
      </header>

      <div className="search-filter-section">
        <div className="search-filter-container">
          <div className="articles-search-wrapper">
            <input
              type="text"
              placeholder="ابحث عن مقال..."
              value={searchTerm}
              onChange={handleSearch}
              className="articles-search-input"
            />
            <Search className="articles-search-icon" />
          </div>

          <div className="categories-container">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => handleCategoryChange(category)}
                className={`category-button ${
                  selectedCategory === category ? 'active' : 'inactive'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="articles-section">
        {loading ? (
          <div className="articles-loading-container">
            <div className="articles-loading-spinner"></div>
            <p className="loading-text">جاري تحميل المقالات...</p>
          </div>
        ) : (
          <>
            <div className="articles-grid">
              {filteredArticles.map(article => (
                <div
                  key={article.id}
                  className="article-card"
                >
                  <div className="article-image-container">
                    <img
                      src={article.image || 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=500'}
                      alt={article.title}
                      className="article-image"
                      onError={(e) => {
                        console.error('فشل تحميل صورة المقال:', article.image);
                        e.target.src = 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=500';
                        
                        // محاولة مسار بديل
                        if (article.image) {
                          const altPath = article.image.replace(
                            'https://darkslateblue-eel-151368.hostingersite.com/storage/',
                            'https://darkslateblue-eel-151368.hostingersite.com/public/storage/'
                          );
                          setTimeout(() => {
                            const tempImg = new Image();
                            tempImg.onload = () => {
                              e.target.src = altPath;
                            };
                            tempImg.src = altPath;
                          }, 100);
                        }
                      }}
                      onLoad={(e) => {
                        console.log('تم تحميل صورة المقال بنجاح:', article.image);
                      }}
                    />
                  </div>
                  
                  <div className="article-content">
                    <h3 className="article-title">
                      {article.title}
                    </h3>
                    
                    {article.category && (
                      <span className="article-category">
                        {article.category}
                      </span>
                    )}
                    
                    
                    
                    {article.read_time && (
                      <p className="article-read-time">
                        ⏱️ {article.read_time}
                      </p>
                    )}
                    
                    <button 
                      className="article-button"
                      onClick={() => handleViewDetails(article.id)}
                    >
                      عرض التفاصيل
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="articles-no-results">
                <p className="articles-no-results-text">
                  {searchTerm || selectedCategory !== 'الكل' 
                    ? 'لا توجد مقالات تطابق بحثك' 
                    : 'لا توجد مقالات متاحة'}
                </p>
                {!searchTerm && selectedCategory === 'الكل' && (
                  <button onClick={fetchArticles} className="retry-button">
                    إعادة التحميل
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MedicalArticles;